import React from 'react';

import { Text } from '@reckon-web/text';

import {
  TaskStatusEnum,
  TaskStatusEnumValue,
} from '../components/work/types/task-status-enum';
import { Dialog } from '../components/common/dialog';
import { useUpdateTaskStatus as _useUpdateTaskStatus } from '../data/useUpdateTaskStatus';
import { processData, useSearchTasks } from '../data/useSearchTasks';
import { useUpdateJobStatus } from '../data/useUpdateJobStatus';
import { GetJobStatusListResult } from '../data/useGetJobStatusList';
import { JobStatusEnum } from '../components/job/types/job-status-enum';
import { JobStatus } from '../../__generated__/ts-gql/@schema';

import { useDialog } from './use-dialog';
import { useTenantTimezoneConversion } from './use-tenant-timezone';
import { useGetVisibleJobStatuses } from './use-get-visible-job-statuses';
import { Task } from './use-query-tasks';

const ProgressJobStatusDialog: React.FC<{
  onClose: (ok?: boolean) => void;
  currentStatus: GetJobStatusListResult;
  inProgressStatus: GetJobStatusListResult;
}> = ({ onClose, currentStatus, inProgressStatus }) => {
  return (
    <Dialog
      title="Change job status?"
      cancelLabel={`Leave as ${currentStatus.name}`}
      onCancel={onClose}
      submitLabel={`Set to ${inProgressStatus.name}`}
      onSubmit={() => onClose(true)}
    >
      <Text>
        The job for this task currently has the status{' '}
        <Text inline weight="semibold">
          {currentStatus.name}
        </Text>
        . Would you like to mark the job for this task as{' '}
        <Text inline weight="semibold">
          {inProgressStatus.name}
        </Text>
        ?
      </Text>
    </Dialog>
  );
};

// // Previously this was using an existing type from one of the query hooks
// // But, now that we want to call this from spots that aren't kanban (and thus aren't exclusively fed by a query)
// // this type lets us be a bit more explicit about what data we do and do not need to do a task status transition
// export type TransitionableTask = {
//   status: TaskStatus;
//   cloudId: string;
//   jobId: string;
//   job: {
//     // WORKFLOW-333 we only need the cloud id of the status, we'll look up the rest
//     status: {
//       cloudId: string;
//     };
//   };
// };

export type TransitionableTask = {
  status: TaskStatusEnumValue;
  jobId: string;
  cloudId: string;
  job: {
    status: {
      cloudId: string;
      status: JobStatus | null;
      orderIndex: number;
    };
  };
};

const NotStartedOrCancelled = new Set<TaskStatusEnumValue>([
  TaskStatusEnum.valuesEnum.NotStarted,
  TaskStatusEnum.valuesEnum.Cancelled,
]);

export const useUpdateTaskStatus = () => {
  const { refetch: searchTasks } = useSearchTasks({ skip: true });
  const [_updateTaskStatus] = _useUpdateTaskStatus();
  const [updateJobStatus] = useUpdateJobStatus();
  const { utcToTenantToLocalDate } = useTenantTimezoneConversion();
  const { data: visibleJobStatuses } = useGetVisibleJobStatuses();

  const [Dialog, openDialog] = useDialog();

  const updateTaskStatus = React.useCallback(
    async ({
      task,
      status,
    }: {
      task: TransitionableTask | Task;
      status: TaskStatusEnumValue;
    }) => {
      if (!visibleJobStatuses || visibleJobStatuses.length === 0) {
        console.warn('useUpdateTaskStatus: no visible job statuses, bailing');
        return false;
      }
      const inProgressStatus = visibleJobStatuses.find(
        (x) => x.status === JobStatusEnum.valuesEnum.InProgress
      );
      if (!inProgressStatus) {
        console.warn(
          'useUpdateTaskStatus: could not find in progress status, bailing',
          [...visibleJobStatuses]
        );
        return false;
      }
      const currentStatus = visibleJobStatuses.find(
        (x) => x.cloudId === task.job.status.cloudId
      );
      if (!currentStatus) {
        console.warn(
          'useUpdateTaskStatus: could not find job current status, bailing',
          [...visibleJobStatuses]
        );
        return false;
      }
      // The task is being progressed from Not Started or Cancelled to any status other than Not Started or Cancelled
      // "The job's Type: JobStatus is has an order index < the JobStatus with the enum of In Progress (Enum: Job Status).
      //   Put another way, the job appears in a column "to the left of" the In Progress column on the kanban board."
      if (
        task.status &&
        NotStartedOrCancelled.has(task.status) &&
        !NotStartedOrCancelled.has(status) &&
        (currentStatus.orderIndex || 0) < (inProgressStatus.orderIndex || 0)
      ) {
        if (!searchTasks) {
          console.warn(
            'useUpdateTaskStatus: searchTasks is not ready, bailing'
          );
          return false;
        }
        const { data } = await searchTasks({
          jobs: [task.jobId || ''],
          myWork: false,
        });
        const tasks = processData(data, utcToTenantToLocalDate);
        // All other tasks in the job are Cancelled or Not Started
        const allTasksNotStartedOrCancelled = tasks?.every((task) =>
          NotStartedOrCancelled.has(task.status)
        );

        if (allTasksNotStartedOrCancelled) {
          // time to prompt them
          const dialogResult = await openDialog<boolean>(
            ({ onClose }) => (
              <ProgressJobStatusDialog
                onClose={onClose}
                currentStatus={currentStatus}
                inProgressStatus={inProgressStatus}
              />
            ),
            {
              size: 'small',
            }
          );

          if (dialogResult) {
            const input = {
              cloudId: task.jobId || '',
              jobStatusId: inProgressStatus.cloudId,
            };

            await updateJobStatus({
              variables: { input },
            });
          }
        }
      }

      await _updateTaskStatus({
        variables: {
          input: {
            cloudId: task.cloudId || '',
            status,
          },
        },
      });
      // got all the way to here, must be happy, one way or another
      return true;
    },
    [
      _updateTaskStatus,
      updateJobStatus,
      openDialog,
      searchTasks,
      utcToTenantToLocalDate,
      visibleJobStatuses,
    ]
  );

  return React.useMemo(
    () =>
      [Dialog, updateTaskStatus] as [typeof Dialog, typeof updateTaskStatus],
    [Dialog, updateTaskStatus]
  );
};
